/**
 * List-icon menu beside each /logs value: edit, delete, and start/end
 * activity-log lock. Anchored dropdown; delete still uses DeleteLog.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import VerticalAlignBottom from '@mui/icons-material/VerticalAlignBottom';
import VerticalAlignTop from '@mui/icons-material/VerticalAlignTop';
import * as MenuS from './ActivityLogsHeaderMenu.styled';
import * as S from './ActivityLogsValueActionsMenu.styled';

export const COMBINE_SKU_ACTIVITY_LOG_LOCK_INFO = 'Turn off Combine SKUs to lock start and end logs.';

const CLOSE_MS = 160;

type ActivityLogsValueActionsMenuProps = {
  rowStartLogId: string,
  rowEndLogId: string,
  rangeStartLogId?: string | null,
  rangeEndLogId?: string | null,
  rangeActive: boolean,
  startLabel: string,
  endLabel: string,
  onSetStart: () => void,
  onSetEnd: () => void,
  onClearStart: () => void,
  onClearEnd: () => void,
  lockDisabled?: boolean,
  lockDisabledReason?: string,
  canEdit?: boolean,
  canDelete?: boolean,
  onDelete?: () => void,
  currency: string,
  editAmount: string,
  editAmountError: string,
  onChangeEditAmount: (value: string) => void,
  onSaveEdit: () => void,
  isEditing: boolean,
  onStartEdit: () => void,
  onCancelEdit: () => void,
  /** Keep the menu up while a confirm modal is open. */
  holdOpen?: boolean,
  onOpenChange?: (open: boolean) => void,
};

const ActivityLogsValueActionsMenu = ({
  rowStartLogId,
  rowEndLogId,
  rangeStartLogId,
  rangeEndLogId,
  rangeActive,
  startLabel,
  endLabel,
  onSetStart,
  onSetEnd,
  onClearStart,
  onClearEnd,
  lockDisabled = false,
  lockDisabledReason = COMBINE_SKU_ACTIVITY_LOG_LOCK_INFO,
  canEdit = false,
  canDelete = false,
  onDelete,
  currency,
  editAmount,
  editAmountError,
  onChangeEditAmount,
  onSaveEdit,
  isEditing,
  onStartEdit,
  onCancelEdit,
  holdOpen = false,
  onOpenChange,
}: ActivityLogsValueActionsMenuProps) => {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const startId = String(rowStartLogId || '').trim();
  const endId = String(rowEndLogId || '').trim();
  const isStart = Boolean(
    !lockDisabled && rangeActive && startId && String(rangeStartLogId || '').trim() === startId,
  );
  const isEnd = Boolean(
    !lockDisabled && rangeActive && endId && String(rangeEndLogId || '').trim() === endId,
  );
  const isLocked = isStart || isEnd;
  const hasEditDelete = canEdit || canDelete;
  const visible = open || leaving;

  const updatePopupPos = () => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    const rect = anchor.getBoundingClientRect();
    const popupH = popup.offsetHeight;
    const popupW = popup.offsetWidth;
    const gap = 6;
    let top = rect.bottom + gap;
    if (top + popupH > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupH - gap);
    }
    let left = rect.right - popupW;
    left = Math.max(8, Math.min(left, window.innerWidth - popupW - 8));
    setPopupPos({ top, left });
  };

  const finishClose = () => {
    setOpen(false);
    setLeaving(false);
    setPopupPos(null);
    onCancelEdit();
    onOpenChange?.(false);
  };

  const closeMenu = () => {
    if (holdOpen) return;
    if (!open && !leaving) return;
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setLeaving(true);
    closeTimer.current = window.setTimeout(finishClose, CLOSE_MS);
  };

  const openMenu = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setLeaving(false);
    setOpen(true);
    onOpenChange?.(true);
  };

  useLayoutEffect(() => {
    if (!visible) return undefined;
    updatePopupPos();
    const onReposition = () => updatePopupPos();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [visible, isEditing, isStart, isEnd, lockDisabled, hasEditDelete, editAmountError]);

  useEffect(() => {
    if (!visible) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (holdOpen) return;
      if (!anchorRef.current?.contains(e.target as Node)
        && !popupRef.current?.contains(e.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [visible, holdOpen, open]);

  useEffect(() => {
    if (!holdOpen || !leaving) return;
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setLeaving(false);
    setOpen(true);
  }, [holdOpen, leaving]);

  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => {
    if (!isEditing || !visible) return undefined;
    const t = window.setTimeout(() => editInputRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [isEditing, visible]);

  const dateTimeLabel = startLabel === endLabel
    ? startLabel
    : `${startLabel} – ${endLabel}`;

  return (
    <S.Anchor ref={anchorRef}>
      <S.Trigger
        type="button"
        $open={open && !leaving}
        $locked={isLocked}
        data-analytics="activityLogs_row_actions_menu"
        aria-label="Row actions"
        aria-expanded={open && !leaving}
        title={isLocked
          ? `${dateTimeLabel} is locked as ${isStart && isEnd ? 'start and end' : isEnd ? 'start' : 'end'} log`
          : 'Row actions'}
        onClick={(e) => {
          e.stopPropagation();
          if (open && !leaving) closeMenu();
          else openMenu();
        }}
      >
        <FormatListBulleted sx={{ fontSize: 14 }} />
      </S.Trigger>
      {visible && createPortal(
        <S.Popup
          ref={popupRef}
          role="menu"
          $leaving={leaving}
          $wide={isEditing}
          style={popupPos
            ? { top: popupPos.top, left: popupPos.left }
            : { visibility: 'hidden' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isEditing ? (
            <S.Panel>
              <S.EditHeader>
                <S.EditTitle>Edit amount</S.EditTitle>
                <S.EditClose
                  type="button"
                  aria-label="Back to actions"
                  title="Back"
                  onClick={() => onCancelEdit()}
                >
                  <Close fontSize="medium" sx={{ scale: '0.65' }} />
                </S.EditClose>
              </S.EditHeader>
              <S.EditBody>
                {editAmountError ? (
                  <S.EditHint $error>{editAmountError}</S.EditHint>
                ) : (
                  <S.EditHint>Enter new amount</S.EditHint>
                )}
                <S.EditInputRow $error={Boolean(editAmountError)}>
                  <S.EditInput
                    ref={editInputRef}
                    value={
                      editAmount
                        ? editAmount === '-'
                          ? editAmount
                          : `${currency}${Number(editAmount).toLocaleString()}`
                        : ''
                    }
                    $amount={Number(editAmount)}
                    onChange={(e) => onChangeEditAmount(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onSaveEdit();
                      }
                    }}
                  />
                  <S.EditSave
                    type="button"
                    data-analytics="activityLogs_row_edit_save"
                    onClick={onSaveEdit}
                  >
                    Save
                  </S.EditSave>
                </S.EditInputRow>
              </S.EditBody>
            </S.Panel>
          ) : (
            <S.Panel>
              {hasEditDelete ? (
                <>
                  <MenuS.SectionLabel>Actions</MenuS.SectionLabel>
                  <MenuS.OptionList>
                    {canEdit ? (
                      <MenuS.OptionRow
                        type="button"
                        role="menuitem"
                        data-analytics="activityLogs_row_edit"
                        onClick={() => onStartEdit()}
                      >
                        <MenuS.OptionIcon>
                          <Edit fontSize="inherit" />
                        </MenuS.OptionIcon>
                        <MenuS.OptionText>
                          <MenuS.OptionTitle>Edit amount</MenuS.OptionTitle>
                          <MenuS.OptionHint>Change the cash value on this log</MenuS.OptionHint>
                        </MenuS.OptionText>
                      </MenuS.OptionRow>
                    ) : null}
                    {canDelete ? (
                      <S.DangerOptionRow
                        type="button"
                        role="menuitem"
                        data-analytics="activityLogs_row_delete"
                        onClick={() => {
                          onDelete?.();
                          closeMenu();
                        }}
                      >
                        <S.DangerOptionIcon>
                          <DeleteOutline fontSize="inherit" />
                        </S.DangerOptionIcon>
                        <MenuS.OptionText>
                          <MenuS.OptionTitle>Delete</MenuS.OptionTitle>
                          <MenuS.OptionHint>Remove this entry from the log</MenuS.OptionHint>
                        </MenuS.OptionText>
                      </S.DangerOptionRow>
                    ) : null}
                  </MenuS.OptionList>
                  <S.Divider />
                </>
              ) : null}
              <MenuS.SectionLabel>Activity log lock</MenuS.SectionLabel>
              {lockDisabled ? (
                <MenuS.DisabledHint>{lockDisabledReason}</MenuS.DisabledHint>
              ) : (
                <MenuS.OptionList>
                  {/*
                    Newest-first table: UI "start" = top (endLogId),
                    UI "end" = bottom (startLogId).
                  */}
                  <MenuS.OptionRow
                    type="button"
                    role="menuitem"
                    $checked={isEnd}
                    data-analytics={isEnd
                      ? 'activityLogs_activity_log_lock_clear_start'
                      : 'activityLogs_activity_log_lock_set_start'}
                    onClick={() => {
                      if (isEnd) onClearEnd();
                      else onSetEnd();
                      closeMenu();
                    }}
                  >
                    <MenuS.OptionIcon>
                      <VerticalAlignTop fontSize="inherit" />
                    </MenuS.OptionIcon>
                    <MenuS.OptionText>
                      <MenuS.OptionTitle>
                        {isEnd ? 'Clear start log' : 'Set as start log'}
                      </MenuS.OptionTitle>
                      <MenuS.OptionHint>
                        {isEnd
                          ? `${endLabel} locks the top — click to unset`
                          : `Lock this log at the top of the range (${endLabel})`}
                      </MenuS.OptionHint>
                    </MenuS.OptionText>
                    <MenuS.OptionCheck $checked={isEnd}>
                      {isEnd ? <Check fontSize="inherit" /> : null}
                    </MenuS.OptionCheck>
                  </MenuS.OptionRow>
                  <MenuS.OptionRow
                    type="button"
                    role="menuitem"
                    $checked={isStart}
                    data-analytics={isStart
                      ? 'activityLogs_activity_log_lock_clear_end'
                      : 'activityLogs_activity_log_lock_set_end'}
                    onClick={() => {
                      if (isStart) onClearStart();
                      else onSetStart();
                      closeMenu();
                    }}
                  >
                    <MenuS.OptionIcon>
                      <VerticalAlignBottom fontSize="inherit" />
                    </MenuS.OptionIcon>
                    <MenuS.OptionText>
                      <MenuS.OptionTitle>
                        {isStart ? 'Clear end log' : 'Set as end log'}
                      </MenuS.OptionTitle>
                      <MenuS.OptionHint>
                        {isStart
                          ? `${startLabel} locks the bottom — click to unset`
                          : `Lock this log at the bottom of the range (${startLabel})`}
                      </MenuS.OptionHint>
                    </MenuS.OptionText>
                    <MenuS.OptionCheck $checked={isStart}>
                      {isStart ? <Check fontSize="inherit" /> : null}
                    </MenuS.OptionCheck>
                  </MenuS.OptionRow>
                </MenuS.OptionList>
              )}
            </S.Panel>
          )}
        </S.Popup>,
        document.body,
      )}
    </S.Anchor>
  );
};

export default ActivityLogsValueActionsMenu;
